import { createLucideIcon, MicOff, Pause, RotateCcw, Sparkle } from 'lucide-react';
import * as React from 'react';
import { AudioMeter, type AudioSpectrumSource } from '../../atoms/AudioMeter';
import { Button } from '../../atoms/Button';
import { VisuallyHidden } from '../../atoms/VisuallyHidden';
import { cn } from '../../lib/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../shadcn/tooltip';
import {
  type RecordingTranscriberLabels,
  type RecordingTranscriberStatus,
  type TranscriptionSourceError,
  type TranscriptPreview,
  type TranscriptSegment,
} from './types';

export type { RecordingTranscriberStatus };

/**
 * Where the pill puts its transcript toggle. `none` leaves the transcript to
 * the host (e.g. a "Notat | Transkripsjon" switch); `start` puts a labelled
 * toggle first; `start-caret` puts a caret first; `end` puts a caret last.
 */
type TranscriptTogglePlacement = 'none' | 'start' | 'start-caret' | 'end';

export interface RecordingTranscriberProps {
  /** The host derives this from its own recording/transcription state. */
  status: RecordingTranscriberStatus;
  /**
   * Elapsed recording time shown in the pill. The host owns the clock — it
   * must keep counting while this component is unmounted, so derive it from
   * wall-clock timestamps, not an in-component interval.
   */
  elapsedSeconds: number;
  /**
   * Committed transcript lines, in order. While idle, a non-empty transcript
   * shows the record and generate buttons, so the doctor can record more or
   * generate again.
   */
  transcript: readonly TranscriptSegment[];
  /** Live, not-yet-committed preview text. */
  preview?: TranscriptPreview;
  /** Spectrum source driving the meter; null/omitted when audio isn't active. */
  analyser?: AudioSpectrumSource | null;
  /**
   * Set when `status` is 'error'; carries the reason for host-side logging.
   * Not read by this component — the error state always shows the generic
   * `labels.micBlocked` copy, regardless of kind.
   */
  error?: TranscriptionSourceError | null;
  /**
   * Fired from the idle record button (a first or a new session on the same
   * consultation), or the retry button in the error state.
   */
  onStart: () => void;
  /** Fired from the pause button while recording. */
  onPause: () => void;
  /** Fired from the resume button while paused. */
  onResume: () => void;
  /**
   * Fired from the generate button while recording, paused, or idle with a
   * transcript. The host stops any active session and generates the note.
   */
  onGenerate: () => void;
  /**
   * Disables the generate button, e.g. when nothing changed since the last
   * generated note. @default false
   */
  generateDisabled?: boolean;
  /** @default 'end' */
  transcriptToggle?: TranscriptTogglePlacement;
  /**
   * Shows the record and generate buttons as icons with tooltips, without
   * visible text. `when-collapsed` shows their text again while the
   * transcript is open, so the panel gets the width of a labelled pill.
   * @default false
   */
  iconOnlyButtons?: boolean | 'when-collapsed';
  /**
   * Extra controls shown before the record button while idle. Gets whether the
   * pill's buttons show as icons only, so the accessory can match them.
   */
  renderIdleAccessory?: (iconOnly: boolean) => React.ReactNode;
  /** Visible/accessible label strings. The host owns translation. */
  labels: RecordingTranscriberLabels;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
}

interface SpeakerGroup {
  speaker?: string;
  segments: TranscriptSegment[];
}

function groupBySpeaker(segments: readonly TranscriptSegment[]): SpeakerGroup[] {
  const groups: SpeakerGroup[] = [];
  for (const segment of segments) {
    const last = groups[groups.length - 1];
    if (last && last.speaker === segment.speaker) {
      last.segments.push(segment);
    } else {
      groups.push({ speaker: segment.speaker, segments: [segment] });
    }
  }
  return groups;
}

// Text color per speaker, assigned by first-appearance order (cycles past 4).
const SPEAKER_COLOR_CLASSES = [
  'text-(--rt-speaker-1)',
  'text-(--rt-speaker-2)',
  'text-(--rt-speaker-3)',
  'text-(--rt-speaker-4)',
] as const;

function buildSpeakerColorIndex(segments: readonly TranscriptSegment[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const { speaker } of segments) {
    const key = speaker ?? '';
    if (!map.has(key)) {
      map.set(key, map.size);
    }
  }
  return map;
}

function noop() {}

// Lucide renders the nodes as a list and takes each React `key` from the node's
// attributes. Without one, React logs "Each child in a list should have a
// unique key prop" from `ForwardRef` on every render.

/** Lucide has no record icon, and `CircleDot`'s 1-unit dot reads as a radio button. */
const RecordIcon = createLucideIcon({
  name: 'record',
  node: [
    ['circle', { cx: '12', cy: '12', r: '7.5', strokeWidth: '1.5', key: 'ring' }],
    ['circle', { cx: '12', cy: '12', r: '5.25', fill: 'currentColor', stroke: 'none', key: 'dot' }],
  ],
});

/** A filled caret; Lucide's chevrons are strokes only. */
const CaretDownIcon = createLucideIcon({
  name: 'caret-down',
  node: [
    [
      'path',
      { d: 'M6.75 9.4h10.5L12 14.6z', fill: 'currentColor', strokeWidth: '1', key: 'caret' },
    ],
  ],
});

/** An icon-only button has no text, so its tooltip opens almost at once. */
const ICON_TOOLTIP_DELAY_MS = 100;

interface TooltipIconButtonProps {
  /** The aria-label, and the tooltip text unless `tooltip` is set. */
  label: string;
  tooltip?: string;
  onClick: () => void;
  disabled?: boolean;
  'aria-expanded'?: boolean;
  children: React.ReactNode;
}

function TooltipIconButton({
  label,
  tooltip = label,
  onClick,
  disabled,
  children,
  ...rest
}: TooltipIconButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="secondary"
            size="icon-xs"
            aria-label={label}
            onClick={onClick}
            disabled={disabled}
          />
        }
        delay={ICON_TOOLTIP_DELAY_MS}
        {...rest}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Transcript expand/collapse state; a fresh idle session (post-finish, or on
 * mount) starts collapsed — the only state this component owns.
 */
function useTranscriptExpanded(
  status: RecordingTranscriberStatus,
): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const [expanded, setExpanded] = React.useState(false);
  const [prevStatus, setPrevStatus] = React.useState(status);
  if (status !== prevStatus) {
    setPrevStatus(status);
    if (status === 'idle') {
      setExpanded(false);
    }
  }
  return [expanded, setExpanded];
}

/** Collapses the transcript on a pointer press outside the recorder. */
function useCollapseOnOutsidePress(
  rootRef: React.RefObject<HTMLDivElement | null>,
  expanded: boolean,
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>,
) {
  React.useEffect(() => {
    if (!expanded) {
      return noop;
    }
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && rootRef.current && !rootRef.current.contains(target)) {
        setExpanded(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [rootRef, expanded, setExpanded]);
}

const EMPTY_PREVIEW: TranscriptPreview = { finalText: '', provisionalText: '' };

/**
 * Presentational recorder pill. Idle, it is a record button. While recording,
 * it shows the timer, a live audio meter, pause and generate. Paused, the
 * meter gives way to resume and generate. Once a transcript exists, the idle
 * pill keeps record and generate, so the doctor can record more or generate
 * the note again. With a transcript toggle, the transcript opens below the
 * pill.
 *
 * Fully controlled. The host owns the microphone, the speech-to-text pipeline,
 * the elapsed-time clock and the transcript/status/analyser state. This
 * component renders that state and reports button presses; it never touches
 * audio APIs or subscribes to anything.
 *
 * The pill sizes to its content. The open transcript grows out of the pill as
 * one surface: an absolutely positioned panel behind the pill row, as wide as
 * the pill, with a floor for readable lines under an icon-only pill.
 * The layout below does not shift when it opens.
 */
export function RecordingTranscriber({
  status,
  elapsedSeconds,
  transcript,
  preview = EMPTY_PREVIEW,
  analyser = null,
  onStart,
  onPause,
  onResume,
  onGenerate,
  generateDisabled = false,
  transcriptToggle = 'end',
  iconOnlyButtons = false,
  renderIdleAccessory,
  labels,
  className,
  ref,
}: RecordingTranscriberProps) {
  const [expanded, setExpanded] = useTranscriptExpanded(status);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  useCollapseOnOutsidePress(rootRef, expanded, setExpanded);

  const toggleTranscript = () => {
    setExpanded((e) => !e);
  };

  const hasTranscript =
    transcript.length > 0 || preview.finalText !== '' || preview.provisionalText !== '';
  const canToggle = transcriptToggle !== 'none' && hasTranscript;
  const toggleFirst = transcriptToggle === 'start' || transcriptToggle === 'start-caret';
  const iconOnly = iconOnlyButtons === 'when-collapsed' ? !expanded : iconOnlyButtons;
  const toggle = (
    <TranscriptToggle
      labels={labels}
      expanded={expanded}
      onClick={toggleTranscript}
      labelled={isToggleLabelled(transcriptToggle, status)}
    />
  );

  return (
    <div
      ref={ref}
      className={cn('recording-transcriber relative inline-block text-left', className)}
      data-status={status}
    >
      <div ref={rootRef}>
        <div className="relative z-50 flex h-10 items-center gap-1.5 rounded-[20px] bg-neutral-100 px-2.5">
          {canToggle && toggleFirst && toggle}
          {status === 'idle' && renderIdleAccessory?.(iconOnly)}
          <PillBody
            status={status}
            iconOnlyButtons={iconOnly}
            hasTranscript={hasTranscript}
            elapsedSeconds={elapsedSeconds}
            analyser={analyser}
            labels={labels}
            generateDisabled={generateDisabled}
            onStart={onStart}
            onPause={onPause}
            onResume={onResume}
            onGenerate={onGenerate}
          />
          {canToggle && transcriptToggle === 'end' && toggle}
        </div>

        {expanded && canToggle && (
          <div className="absolute top-0 right-0 z-40 w-72 max-w-[calc(100vw-2rem)] min-w-full overflow-hidden rounded-[20px] bg-neutral-100 pt-10 shadow-lg">
            <RecordingTranscript
              segments={transcript}
              preview={preview}
              className="max-h-75 pt-2"
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface PillBodyProps {
  status: RecordingTranscriberStatus;
  iconOnlyButtons: boolean;
  hasTranscript: boolean;
  elapsedSeconds: number;
  analyser: AudioSpectrumSource | null;
  labels: RecordingTranscriberLabels;
  generateDisabled: boolean;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onGenerate: () => void;
}

function PillBody({
  status,
  iconOnlyButtons,
  hasTranscript,
  elapsedSeconds,
  analyser,
  labels,
  generateDisabled,
  onStart,
  onPause,
  onResume,
  onGenerate,
}: PillBodyProps) {
  const generate = (
    <GenerateButton
      labels={labels}
      disabled={generateDisabled}
      onClick={onGenerate}
      iconOnly={iconOnlyButtons || status === 'recording'}
    />
  );

  switch (status) {
    case 'idle':
      return (
        <>
          <RecordButton label={labels.record} onClick={onStart} iconOnly={iconOnlyButtons} />
          {hasTranscript && generate}
        </>
      );
    case 'recording':
      return (
        <>
          <Timer seconds={elapsedSeconds} />
          {/* The canvas fills its box; in a content-sized pill it needs a fixed one. */}
          <span className="block h-6 w-24 flex-none">
            <AudioMeter analyser={analyser} active columns={19} rows={5} pitch={5} dotSize={4} />
          </span>
          <TooltipIconButton label={labels.pause} onClick={onPause}>
            <Pause className="size-[15px]" aria-hidden="true" fill="currentColor" />
          </TooltipIconButton>
          {generate}
        </>
      );
    case 'paused':
      return (
        <>
          <RecordButton label={labels.resume} onClick={onResume} iconOnly={iconOnlyButtons} />
          {generate}
        </>
      );
    case 'reconnecting':
    case 'generating':
      return (
        <>
          <Timer seconds={elapsedSeconds} />
          <span
            className="truncate px-1 text-[13px] text-(--rt-foreground-muted)"
            aria-live="polite"
          >
            {status === 'reconnecting' ? labels.reconnecting : labels.generating}
          </span>
        </>
      );
    case 'error':
      return (
        <>
          <span className="flex items-center gap-1.5 px-1 text-[13px] text-(--rt-foreground-muted)">
            <MicOff className="size-4 flex-none" aria-hidden="true" />
            <span className="truncate">{labels.micBlocked}</span>
          </span>
          <TooltipIconButton label={labels.retry} onClick={onStart}>
            <RotateCcw className="size-4" aria-hidden="true" />
          </TooltipIconButton>
        </>
      );
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}

function Timer({ seconds }: { seconds: number }) {
  return (
    <span className="min-w-10 px-1 text-sm font-semibold text-neutral-900 tabular-nums select-none">
      {formatTime(seconds)}
    </span>
  );
}

/** Starts, resumes or adds to a recording. */
interface RecordButtonProps {
  label: string;
  onClick: () => void;
  iconOnly: boolean;
}

function RecordButton({ label, onClick, iconOnly }: RecordButtonProps) {
  if (iconOnly) {
    return (
      <TooltipIconButton label={label} onClick={onClick}>
        <RecordIcon aria-hidden="true" />
      </TooltipIconButton>
    );
  }
  return (
    <Button variant="secondary" size="xs" onClick={onClick} className="flex-none">
      <RecordIcon aria-hidden="true" />
      {label}
    </Button>
  );
}

interface GenerateButtonProps {
  labels: RecordingTranscriberLabels;
  disabled: boolean;
  onClick: () => void;
  /** While recording, the pill has room only for the icon. */
  iconOnly: boolean;
}

function GenerateButton({ labels, disabled, onClick, iconOnly }: GenerateButtonProps) {
  const icon = <Sparkle aria-hidden="true" fill="currentColor" />;
  if (iconOnly) {
    return (
      <TooltipIconButton label={labels.generate} onClick={onClick} disabled={disabled}>
        {icon}
      </TooltipIconButton>
    );
  }
  return (
    <Button
      variant="secondary"
      size="xs"
      aria-label={labels.generate}
      onClick={onClick}
      disabled={disabled}
      className="flex-none"
    >
      {icon}
      {labels.generateShort}
    </Button>
  );
}

/**
 * The `start` toggle shows its text while idle or paused; the meter needs the
 * room while recording. The `start-caret` toggle shows its text only while
 * paused.
 */
function isToggleLabelled(
  placement: TranscriptTogglePlacement,
  status: RecordingTranscriberStatus,
): boolean {
  switch (placement) {
    case 'start':
      return status === 'idle' || status === 'paused';
    case 'start-caret':
      return status === 'paused';
    case 'none':
    case 'end':
      return false;
    default: {
      const exhaustive: never = placement;
      return exhaustive;
    }
  }
}

interface TranscriptToggleProps {
  labels: RecordingTranscriberLabels;
  expanded: boolean;
  onClick: () => void;
  labelled: boolean;
}

function TranscriptToggle({ labels, expanded, onClick, labelled }: TranscriptToggleProps) {
  const caret = (
    <CaretDownIcon
      aria-hidden="true"
      className={cn('transition-transform', expanded && 'rotate-180')}
    />
  );
  const tooltip = expanded ? labels.hideTranscript : labels.showTranscript;
  if (labelled) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="secondary"
              size="xs"
              aria-label={labels.toggleTranscript}
              aria-expanded={expanded}
              onClick={onClick}
            />
          }
          delay={ICON_TOOLTIP_DELAY_MS}
        >
          {caret}
          <span className="max-sm:sr-only">{labels.transcript}</span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <TooltipIconButton
      label={labels.toggleTranscript}
      tooltip={tooltip}
      aria-expanded={expanded}
      onClick={onClick}
    >
      {caret}
    </TooltipIconButton>
  );
}
interface RecordingTranscriptProps {
  segments: readonly TranscriptSegment[];
  preview?: TranscriptPreview;
  /** Shown while there is no line yet, for screen readers. */
  emptyLabel?: string;
  className?: string;
}

/**
 * The streaming transcript as a scrolling log: one paragraph per speaker turn,
 * speakers told apart by color. It keeps the newest line in view. The pill's
 * dropdown uses it.
 */
function RecordingTranscript({
  segments,
  preview = EMPTY_PREVIEW,
  emptyLabel = 'No transcript yet',
  className,
}: RecordingTranscriptProps) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  // Keyed on the streaming text, not the preview object, so an identical
  // re-render doesn't yank the scroll position.
  const streamingText = preview.finalText + preview.provisionalText;
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || (segments.length === 0 && streamingText === '')) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [segments, streamingText]);

  const groups = groupBySpeaker(segments);
  const speakerColorIndex = buildSpeakerColorIndex(segments);
  const hasContent = segments.length > 0 || streamingText !== '';

  const colorFor = (speaker: string | undefined): string => {
    const index = speakerColorIndex.get(speaker ?? '') ?? 0;
    return SPEAKER_COLOR_CLASSES[index % SPEAKER_COLOR_CLASSES.length];
  };

  return (
    <div
      ref={scrollRef}
      role="log"
      aria-live="polite"
      aria-atomic="false"
      className={cn(
        'recording-transcriber-scroll animate-fade-in overflow-y-auto px-4 pt-1.5 pb-3.5 text-sm/relaxed font-semibold text-(--rt-foreground-muted)',
        className,
      )}
    >
      {!hasContent && <VisuallyHidden>{emptyLabel}</VisuallyHidden>}
      {/* Speakers are distinguished by color (not labels) to save vertical space. */}
      {groups.map((group, gi) => (
        <p
          key={group.segments[0]?.id ?? gi}
          className={cn('mb-2 last:mb-0', colorFor(group.speaker))}
        >
          {group.segments.map((s) => s.text).join(' ')}
        </p>
      ))}
      {(preview.finalText || preview.provisionalText) && (
        <p className={cn('mt-1', colorFor(preview.speaker))}>
          {preview.finalText && <span>{preview.finalText}</span>}
          {preview.provisionalText && (
            <span className="opacity-60">
              {preview.finalText ? ' ' : ''}
              {preview.provisionalText}
            </span>
          )}
        </p>
      )}
    </div>
  );
}
